import { createSelector } from '@ngrx/store';

import { Contact, ContactPerson, Language } from '@mobi/mcs-schadenmeldung-types-ng-jslib';

import { GaOrganisationUnit } from '../../../features/osm/shared/service/mcs-schadenmeldung-unified-integration.service.types';
import { OsmStep } from '../../../features/osm/shared/state/models/osm.state.model';
import * as fromOsm from '../../../features/osm/shared/state/selectors/osm.selectors';
import { StoredContact } from '../../../state/models/core.types';
import * as fromCore from '../../../state/selectors/core.selectors';
import { selectorType } from '../../../state/state.util';

export const selectFooterView = createSelector(
    fromCore.selectContact,
    fromCore.selectLanguage,
    fromCore.selectPortalToken,
    fromOsm.selectCurrentStep,
    (contact, language, token, currentStep) => {
        // use token as "portal" or "isprotekta" ist initially wrong
        return {
            contact: token === 'mobi' ? adjustContact(contact, language) : undefined,
            mobiFallback: token === 'mobi' ? adjustNoContact(language) : undefined,
            protekta: token === 'protekta',
            isSuccess: currentStep === OsmStep.SUCCESS,
            language,
        };
    },
);

export interface ContactFallback {
    webpage: string;
}

export type FooterView = selectorType<typeof selectFooterView>;

function adjustContact(contact: StoredContact, language: Language) {
    const person = contact && 'person' in contact ? contact.person : undefined;
    return (person ?? contact?.organisationUnit)
        ? ({
              person: adjustPerson(person, language),
              organisationUnit: adjustOrganisationUnit(contact?.organisationUnit, language),
          } as Contact)
        : undefined;
}

function adjustPerson(person: ContactPerson, language: Language) {
    return (
        person && {
            ...person,
            jobTitles: person.jobTitles && {
                ...person.jobTitles,
                jobTitle: person.jobTitles[language] ?? person.jobTitles.jobTitle,
            },
            emails: person.emails && {
                ...person.emails,
                email: person.emails[language] ?? person.emails.email,
            },
        }
    );
}

function adjustOrganisationUnit(organisationUnit: GaOrganisationUnit, language: Language) {
    return (
        organisationUnit && {
            ...organisationUnit,
            names: organisationUnit.names && {
                ...organisationUnit.names,
                name: organisationUnit.names[language] ?? organisationUnit.names.name,
            },
            webpages: organisationUnit.webpages && {
                ...organisationUnit.webpages,
                webpage: organisationUnit.webpages[language] ?? organisationUnit.webpages.webpage,
            },
            addresses: organisationUnit.addresses && {
                ...organisationUnit.addresses,
                address: organisationUnit.addresses[language] ?? organisationUnit.addresses.address,
            },
        }
    );
}

function adjustNoContact(language: Language): ContactFallback {
    const fallbackPages = {
        de: 'https://www.mobiliar.ch/versicherungen-und-vorsorge/generalagenturen',
        fr: 'https://www.mobiliere.ch/assurances-et-prevoyance/agences-generales',
        it: 'https://www.mobiliare.ch/assicurazioni-e-previdenza/agenzie-generali',
    };
    return {
        webpage: fallbackPages[language] ?? fallbackPages.de,
    };
}
